/**
 * Users Page - Business Logic Layer
 *
 * Responsibilities:
 * - Data fetching (React Query hooks)
 * - State management (search, pagination, modals)
 * - Event handlers
 * - Table column definitions
 * - Props preparation for template
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUsers } from '@/features/users/hooks/useUsers'
import { User } from '@/features/users/api'
import { useDebounce } from '@/hooks/use-debounce'
import { PageLayout } from '@/layouts/PageLayout'
import { UsersTemplate } from '@/templates/UsersTemplate'
import { ColumnDef } from '@/components/shared/DataTable/DataTable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Users, Search, Download, Loader2, Eye, Coins, RefreshCw, Settings, Bot, User as UserIcon } from 'lucide-react'

export default function UsersPage() {
  // ========================================
  // BUSINESS LOGIC
  // ========================================

  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [emailSearch, setEmailSearch] = useState('')
  const [isDownloading, setIsDownloading] = useState(false)
  const limit = 10

  // Debounce email search to avoid too many API calls
  const debouncedEmailSearch = useDebounce(emailSearch, 500)

  const { data, isLoading, error, refetch, isRefetching } = useUsers(
    page,
    limit,
    undefined,
    undefined,
    undefined,
    debouncedEmailSearch || undefined
  )

  // Modals state
  const [topUpUser, setTopUpUser] = useState<User | null>(null)
  const [refillSettingsUser, setRefillSettingsUser] = useState<User | null>(null)
  const [configUser, setConfigUser] = useState<User | null>(null)

  // Event handlers
  const handleRefresh = () => {
    refetch()
  }

  const handleDownloadCSV = async () => {
    setIsDownloading(true)
    try {
      const { v1UsersApi } = await import('@/features/users/api')
      const blob = await v1UsersApi.exportCSV(undefined, debouncedEmailSearch || undefined)

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      alert('Failed to download users data. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  // Define table columns
  const columns: ColumnDef<User>[] = [
    {
      key: 'user',
      header: 'User',
      cell: (user) => (
        <div className="flex items-center gap-2">
          {user.avatar ? (
            <img
              src={`https://agents.iamsaif.ai${user.avatar}`}
              alt={user.name || user.email}
              className="h-8 w-8 rounded-full"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              <UserIcon className="h-4 w-4" />
            </div>
          )}
          <div>
            <div className="font-medium">{user.name || user.username || 'No name'}</div>
            {user.emailVerified === false && (
              <div className="text-xs text-muted-foreground">Email not verified</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      cell: (user) => user.email,
    },
    {
      key: 'role',
      header: 'Role',
      cell: (user) => (
        <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
          {user.role}
        </Badge>
      ),
    },
    {
      key: 'balance',
      header: 'Balance',
      headerClassName: 'text-right',
      className: 'text-right font-mono',
      cell: (user) =>
        user.tokenCredits !== undefined
          ? `${user.tokenCredits.toFixed(2)} tokens`
          : '0.00 tokens',
    },
    {
      key: 'total_agents',
      header: 'Total Agents',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (user) => (
        <div className="flex items-center justify-end gap-1">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{user.total_agents || 0}</span>
        </div>
      ),
    },
    {
      key: 'tokens_consumed',
      header: 'Tokens Consumed',
      headerClassName: 'text-right',
      className: 'text-right font-mono',
      cell: (user) =>
        user.tokens_consumed !== undefined
          ? user.tokens_consumed.toLocaleString()
          : '0',
    },
    {
      key: 'cost',
      header: 'Cost',
      headerClassName: 'text-right',
      className: 'text-right font-mono',
      cell: (user) =>
        user.cost !== undefined
          ? `$${user.cost.toFixed(2)}`
          : '$0.00',
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (user) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/users/${user._id}`)}
          >
            <Eye className="mr-1 h-4 w-4" />
            Details
          </Button>
          <Button size="sm" onClick={() => setTopUpUser(user)}>
            <Coins className="mr-1 h-4 w-4" />
            Top-Up
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setRefillSettingsUser(user)}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Refill
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfigUser(user)}>
            <Settings className="mr-1 h-4 w-4" />
            Config
          </Button>
        </div>
      ),
    },
  ]

  // ========================================
  // LAYOUT + TEMPLATE COMPOSITION
  // ========================================

  return (
    <PageLayout
      title="Users"
      description="SAIFChat user management (v1)"
      icon={<Users className="h-6 w-6" />}
      onRefresh={handleRefresh}
      isRefreshing={isRefetching}
      actions={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email..."
              value={emailSearch}
              onChange={(e) => {
                setEmailSearch(e.target.value)
                setPage(1)
              }}
              className="pl-8 w-[300px]"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCSV}
            disabled={isDownloading || isLoading}
          >
            {isDownloading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download CSV
              </>
            )}
          </Button>
        </div>
      }
    >
      <UsersTemplate
        users={data?.users || []}
        total={data?.total || 0}
        page={data?.page || 1}
        limit={limit}
        totalPages={data?.totalPages || 1}
        isLoading={isLoading}
        error={error}
        topUpUser={topUpUser}
        refillSettingsUser={refillSettingsUser}
        configUser={configUser}
        onSetTopUpUser={setTopUpUser}
        onSetRefillSettingsUser={setRefillSettingsUser}
        onSetConfigUser={setConfigUser}
        columns={columns}
        onPageChange={setPage}
        onRefresh={handleRefresh}
      />
    </PageLayout>
  )
}
